<template>
  <Loader :visible="isLoggingIn" :text="t('login.loading')"></Loader>
  <div class="login-form" v-if="!isLoggingIn">
    <div>
      <Icon
        :model="
          appStore.currentMode() === DisplayMode.Light
            ? appStore.configuration?.components.logotype.light
            : appStore.configuration?.components.logotype.dark
        "
        :alt="t('logotype.alt')"
        :key="componentKey"
        class="logotype"
      />
    </div>
    <h2>
      {{ t('loginForm.title') }} <br />
      {{ t('loginForm.subtitle') }}
    </h2>

    <!-- OAuth Providers -->
    <div class="oauth-buttons">
      <ButtonLoginWith
        :provider="OauthOption.Microsoft"
        :model="appStore.configuration?.login.loginForm.buttonLoginWith"
        :alt="t('loginForm.buttonLoginWith.alt')"
        @click="loginWithAsync(OauthOption.Microsoft)"
        :text="t('loginForm.buttonLoginWith.text')"
        :isDisabled="false"
        :key="componentKey"
        :show="oauthOptions.includes(OauthOption.Microsoft)"
      >
      </ButtonLoginWith>
      <ButtonLoginWith
        :provider="OauthOption.Google"
        :model="appStore.configuration?.login.loginForm.buttonLoginWith"
        :alt="t('loginForm.buttonLoginWith.alt')"
        @click="loginWithAsync(OauthOption.Google)"
        :text="t('loginForm.buttonLoginWith.text')"
        :isDisabled="false"
        :key="componentKey"
        :show="oauthOptions.includes(OauthOption.Google)"
      >
      </ButtonLoginWith>
      <ButtonLoginWith
        :provider="OauthOption.Salesforce"
        :model="appStore.configuration?.login.loginForm.buttonLoginWith"
        :alt="t('loginForm.buttonLoginWith.alt')"
        @click="loginWithAsync(OauthOption.Salesforce)"
        :text="t('loginForm.buttonLoginWith.text')"
        :isDisabled="false"
        :key="componentKey"
        :show="oauthOptions.includes(OauthOption.Salesforce)"
      >
      </ButtonLoginWith>
      <ButtonLoginWith
        :provider="OauthOption.Okta"
        :model="appStore.configuration?.login.loginForm.buttonLoginWith"
        :alt="t('loginForm.buttonLoginWith.alt')"
        @click="loginWithAsync(OauthOption.Okta)"
        :text="t('loginForm.buttonLoginWith.text')"
        :isDisabled="false"
        :key="componentKey"
        :show="oauthOptions.includes(OauthOption.Okta)"
      >
      </ButtonLoginWith>
    </div>

    <p
      class="separator"
      v-if="oauthOptions.includes(OauthOption.EmailAndPassword) && oauthOptions.length >= 2"
    >
      {{ t('loginForm.emailAndPassword.header') }}
    </p>

    <div class="login-actions">
      <!-- Email + Password -->
      <form
        @submit.prevent="loginWithEmailAsync"
        v-if="oauthOptions.includes(OauthOption.EmailAndPassword)"
      >
        <input
          type="email"
          v-model="email"
          :placeholder="t('loginForm.emailAndPassword.input.email.placeholderText')"
          required
        />

        <div class="password-field">
          <input
            :type="showPassword ? 'text' : 'password'"
            v-model="password"
            :placeholder="t('loginForm.emailAndPassword.input.password.placeholderText')"
            required
          />
          <button
            type="button"
            class="toggle-password"
            @click="togglePassword"
            aria-label="Show or hide password"
          >
            <span class="icon">
              <img
                :src="
                  appStore.currentMode() === DisplayMode.Light ? passwordEyeLight : passwordEyeDark
                "
                :alt="t('loginForm.emailAndPassword.input.password.togglePasswordAlt')"
              />
            </span>
          </button>
        </div>

        <button
          type="submit"
          class="login-btn"
          :alt="t('loginForm.emailAndPassword.loginButton.alt')"
        >
          {{ t('loginForm.emailAndPassword.loginButton.text') }}
        </button>
      </form>

      <button
        v-if="cancelButtonVisible"
        type="button"
        class="cancel-btn"
        :alt="t('loginForm.cancelButton.alt')"
        @click="onCancel"
      >
        {{ t('loginForm.cancelButton.text') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import passwordEyeLight from '../assets/icons/light/icon-password-eye.svg'
import passwordEyeDark from '../assets/icons/dark/icon-password-eye.svg'
import Icon from '../components/Icon.vue'
import { OauthOption } from '../types/auth/oauthOption'
import { useAppStore } from '../stores/appStore'
import { useUserProfileStore } from '../stores/userProfileStore'
import { UserProfileData } from '../types/userProfile/userProfileData'
import { ref, computed, onMounted } from 'vue'
import { t } from '../composables/Translation'
import { useRouter } from 'vue-router'
import { State } from '../types/app/state'
import { signInWithPasswordAsync } from '../composables/auth/authSignInWithPasswordAsync'
import { signInCallbackAsync } from '../composables/auth/authSignInCallbackAsync'
import { signInWithGoogleAsync } from '../composables/auth/authSignInWithGoogleAsync'
import type { AuthData } from '../types/auth/authData'
import ButtonLoginWith from '../components/ButtonLoginWith.vue'
import axios from 'axios'
import { LoginRequired } from '../types/app/loginRequired'
import { DisplayMode } from '../types/app/displayMode'
import { signInWithOktaAsync } from '../composables/auth/authSignInWithOktaAsync'
import { normalizeAuthData } from '../composables/auth/normalizeAuthData'
import { useChatStore } from '../stores/chatStore'
import { useAidaSessionStore } from '../stores/aidaSessionStore'
import Loader from '../components/Loader.vue'
import { useToastStore } from '../stores/toastStore'
import { signInWithSalesforceAsync } from '../composables/auth/authSignInWithSalesforceAsync'
import { markAutoLoginAttempt, canAutoLogin } from '../composables/auth/autoLoginGuard'

const router = useRouter()
const toast = useToastStore()

const appStore = useAppStore()
const chatStore = useChatStore()
const userProfileStore = useUserProfileStore()
const aidaSessionStore = useAidaSessionStore()
const emit = defineEmits<{ (e: 'login-successful'): void }>()
const isLoggingIn = ref(true)
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const cancelButtonVisible = ref(false)

let componentKey = ref(0)

const oauthOptions = computed<OauthOption[]>(() => {
  return appStore.configuration?.login.oauthOptions ?? []
})

function togglePassword() {
  showPassword.value = !showPassword.value
}

async function loginWithAsync(provider: OauthOption) {
  console.log(`Login with ${provider}`)

  try {
    appStore.setOauthProvider(provider)

    if (provider === OauthOption.Google) {
      // Google = popup, completes in-place
      const authData = await signInWithGoogleAsync()
      isLoggingIn.value = true
      const normalized = normalizeAuthData(authData, provider)
      const userProfile = new UserProfileData(normalized, provider)
      await signInCallbackAsync(userProfile)
      userProfileStore.setUserProfile(userProfile)
      if (appStore.currentState !== State.AttemptToOpenSR) appStore.setState(State.Main)
      router.push({ name: 'main' })
    } else if (provider === OauthOption.Okta || provider === OauthOption.Salesforce) {
      isLoggingIn.value = true
      markAutoLoginAttempt()
      saveStoresForRedirect()

      if (provider === OauthOption.Okta) await signInWithOktaAsync()
      else await signInWithSalesforceAsync()

      return
    }
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status
      if (status === 400) {
        alert(t('login.invalidCredentialsError'))
      } else {
        console.error('Unexpected login error:', status, err.message)
      }
    } else {
      console.error('Unexpected non-Axios error:', err)
    }
    toast.error(t('login.genericError'))

    isLoggingIn.value = false
  }
}

async function loginWithEmailAsync() {
  console.log('Login with email:', email.value)
  try {
    isLoggingIn.value = true
    const authData = (await signInWithPasswordAsync(email.value, password.value)) as AuthData
    if (authData != null) {
      const normalized = normalizeAuthData(authData, OauthOption.EmailAndPassword)
      const userProfile = new UserProfileData(normalized, OauthOption.EmailAndPassword)
      await signInCallbackAsync(userProfile)
      userProfileStore.setUserProfile(userProfile)
    } else {
      console.log('No profile data after login')
    }
    console.log('profile: ', userProfileStore.profile?.displayName)
    if (appStore.currentState !== State.AttemptToOpenSR) appStore.setState(State.Main)
    emit('login-successful')
    router.push({ name: 'main' })
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status
      if (status === 400) {
        alert(t('login.invalidCredentialsError'))
      } else {
        console.error('Unexpected login error:', status, err.message)
      }
    } else {
      console.error('Unexpected non-Axios error:', err)
    }
    toast.error(t('login.genericError'))
  } finally {
    isLoggingIn.value = false
  }
}

function saveStoresForRedirect() {
  chatStore.saveForRedirect()
  appStore.saveForRedirect()
  aidaSessionStore.saveForRedirect()
}

function onCancel() {
  if (appStore.configuration?.login.isLoginRequired !== LoginRequired.Always) {
    appStore.setState(State.Main)
    router.push({ name: 'main' })
  } else {
    appStore.setState(State.Login)
    router.push({ name: 'login' })
  }
}

onMounted(async () => {

  if (appStore.currentState !== State.AttemptToOpenSR) appStore.setState(State.Login)
  if (appStore.configuration?.login.isLoginRequired !== LoginRequired.Always)
    cancelButtonVisible.value = true

  const availableOauthProviders =
    appStore.configuration?.login.oauthOptions.filter(x => x !== OauthOption.EmailAndPassword) ??
    null

  const shouldAutoLogin =
    appStore.configuration?.login.isAutomaticLogin &&
    availableOauthProviders?.length == 1 &&
    !appStore.configuration?.login.oauthOptions.includes(OauthOption.EmailAndPassword)

  const canLogin = canAutoLogin()
  if (shouldAutoLogin && availableOauthProviders[0] && canLogin) {
    await loginWithAsync(availableOauthProviders[0])
    return
  }

  if (shouldAutoLogin && !canLogin) {
    toast.error(t('login.autoLoginError'))
    router.replace({ name: 'loginFailed' })
  }

  isLoggingIn.value = false
})
</script>

<style scoped lang="scss">
.login-form {
  width: 100%;
  max-width: 768px;
  margin: 0 auto;
  padding: 0 16px;
  text-align: center;
  color: var(--text-color);
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
}

h2 {
  font-size: 36px;
  font-weight: 500;
  margin-bottom: 32px;
  margin-top: 24px;
  max-width: 768px;
}

.oauth-buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 388px;
  margin-bottom: 21px;
}

.oauth-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 12px;
  background: var(--background-color);
  cursor: pointer;
  font-size: 13px;
  height: 34px;
}

.oauth-btn:hover {
  background: #f2f2f2;
}

.separator {
  font-size: 14px;
  margin-bottom: 21px;
}

form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 388px;
  margin: 0 auto;
}

.login-actions {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 388px;
  margin: 0 auto;
}

input {
  width: 100%;
  box-sizing: border-box;
  padding-left: 10px;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  font-size: 14px;
  height: 40px;
}

.password-field {
  display: flex;
  align-items: center;
  position: relative;
}

.password-field input {
  flex: 1;
}

.toggle-password {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
}

.login-btn {
  font-size: 14px;
  background: var(--text-background-color);
  color: var(--light-color);
  border: none;
  padding: 10px;
  border-radius: 12px;
  cursor: pointer;
  height: 40px;
}

.login-btn:hover {
  background: var(--menu-item-color);
  color: var(--border-color);
}

.logotype {
  width: 200px;
  margin: auto;
  margin-top: 10px;
  padding-top: 0px;
  padding-bottom: 0px;
}

.cancel-btn {
  font-size: 14px;
  width: 100%;
  height: 40px;
  padding: 10px;
  border-radius: 12px;
  cursor: pointer;
  transition:
    background-color 0.2s,
    border-color 0.2s;

  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-color);
}

.cancel-btn:hover {
  background: var(--menu-item-color);
  color: var(--border-color);
  border-color: var(--menu-item-color);
}
</style>
